import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '@/lib/api';
import MobileShell from '@/components/MobileShell';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/serviceTimer';
import { openRazorpayCheckout } from '@/lib/razorpay';

export default function ConsumerBookingExtraPayment() {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/bookings/${bookingId}`);
      setBooking(data.booking);
    } catch {
      toast.error('Could not load booking');
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    load();
  }, [load]);

  const pay = async () => {
    if (!booking?.extra_settlement) return;
    setPaying(true);
    try {
      const { data } = await api.post(`/bookings/${bookingId}/extra-payment/order`, {});
      const checkout = data.checkout;
      await openRazorpayCheckout({
        checkout,
        description: 'Extra service payment',
        onSuccess: async (response) => {
          try {
            await api.post(`/bookings/${bookingId}/extra-payment/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success('Extra payment successful');
            await load();
            window.location.href = `/consumer/bookings/${bookingId}/tracking`;
          } catch (e) {
            toast.error(e.response?.data?.detail || 'Payment verification failed');
          } finally {
            setPaying(false);
          }
        },
        onDismiss: () => setPaying(false),
        onFailed: () => {
          toast.error('Payment failed');
          setPaying(false);
          load();
        },
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start payment');
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <MobileShell title="Extra Payment">
        <div className="p-5 text-sm text-slate-500">Loading…</div>
      </MobileShell>
    );
  }

  if (!booking) {
    return (
      <MobileShell title="Extra Payment">
        <div className="p-5 text-sm text-slate-500">Booking not found.</div>
      </MobileShell>
    );
  }

  const settlement = booking.extra_settlement;
  const isPaid =
    booking.status === 'completed' ||
    booking.extra_payment_completed_at ||
    booking.extra_payment?.status === 'paid';
  const currency = settlement?.currency || 'INR';
  const total = settlement?.final_extra_customer_amount ?? 0;

  return (
    <MobileShell title="Extra Payment">
      <div className="px-5 py-5 space-y-4" data-testid="extra-payment-page">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-slate-700">Status</span>
          <span
            data-testid="extra-payment-status"
            className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
              isPaid ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
            }`}
          >
            {isPaid ? 'Payment complete' : 'Payment required'}
          </span>
        </div>

        {settlement && (
          <div className="resqly-card p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Extra service</span>
              <span>{formatCurrency(settlement.raw_extra_service_amount, currency)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">
                Tax{settlement.extra_tax_percent != null ? ` (${settlement.extra_tax_percent}%)` : ''}
              </span>
              <span>{formatCurrency(settlement.extra_tax_amount ?? 0, currency)}</span>
            </div>
            <div className="border-t pt-2 flex justify-between text-sm font-semibold">
              <span>Total additional payment</span>
              <span data-testid="extra-payment-total">{formatCurrency(total, currency)}</span>
            </div>
          </div>
        )}

        {!isPaid && settlement && total > 0 && (
          <Button
            data-testid="extra-pay-button"
            className="w-full bg-slate-900 hover:bg-slate-800"
            disabled={paying}
            onClick={pay}
          >
            {paying ? 'Opening checkout…' : `Pay ${formatCurrency(total, currency)}`}
          </Button>
        )}

        <Link
          to={`/consumer/bookings/${bookingId}/tracking`}
          className="block text-center text-sm text-blue-700 font-semibold py-2"
        >
          Back to booking
        </Link>
      </div>
    </MobileShell>
  );
}
